/* eslint-disable @typescript-eslint/no-explicit-any */
import { useEffect, useState } from "react";
import { useDarkMode } from "../context/DarkModeContext";

// Define the props interface
interface ContactApplicantModalProps {
  isOpen: boolean;
  onClose: () => void;
  application: any;
  onContact: (data: ContactData) => Promise<void>;
  currentUser: any;
}

// Update the interface and state
interface ContactData {
  subject: string;
  message: string;
  channel: "email" | "whatsapp" | "both";
  sendEmail: boolean;
  sendWhatsApp: boolean;
  applicantEmail?: string;
  applicantPhone?: string;
  applicantName?: string;
  jobTitle?: string;
  companyName?: string;
  currentUserName?: string;
}

export default function ContactApplicantModal({
  isOpen,
  onClose,
  application,
  onContact,
  currentUser,
}: ContactApplicantModalProps) {
  const { isDark } = useDarkMode();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const [formData, setFormData] = useState<ContactData>({
    subject: "",
    message: "",
    channel: "email",
    sendEmail: true,
    sendWhatsApp: false,
    applicantEmail: application?.user?.email || "",
    applicantPhone: application?.user?.profile?.phone || "",
    applicantName: application?.user?.name || "",
    jobTitle: application?.job?.title || "",
    companyName: application?.job?.company || "",
    currentUserName: currentUser?.name || "",
  });

  // Update formData when application changes
  useEffect(() => {
    if (application && isOpen) {
      setFormData((prev) => ({
        ...prev,
        applicantEmail: application?.user?.email || prev.applicantEmail,
        applicantPhone:
          application?.user?.profile?.phone || prev.applicantPhone,
        applicantName: application?.user?.name || prev.applicantName,
        jobTitle: application?.job?.title || prev.jobTitle,
        companyName: application?.job?.company || prev.companyName,
      }));
    }
  }, [application, isOpen]);

  const handleChannelChange = (channel: "email" | "whatsapp" | "both") => {
    setFormData({
      ...formData,
      channel,
      sendEmail: channel === "email" || channel === "both",
      sendWhatsApp: channel === "whatsapp" || channel === "both",
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validate based on selected channel
    if (formData.sendEmail) {
      if (!formData.applicantEmail?.trim()) {
        setError("Applicant email is required for email messages");
        return;
      }
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(formData.applicantEmail)) {
        setError("Please enter a valid email address");
        return;
      }
    }

    if (formData.sendWhatsApp) {
      if (!formData.applicantPhone?.trim()) {
        setError("Applicant phone number is required for WhatsApp messages");
        return;
      }
      // Basic phone validation for Kenya (you can adjust for other countries)
      const phoneRegex = /^(?:\+254|0)[17]\d{8}$/;
      if (!phoneRegex.test(formData.applicantPhone.replace(/\s/g, ""))) {
        setError(
          "Please enter a valid Kenyan phone number (format: 0712 345 678 or +254712345678)",
        );
        return;
      }
    }

    if (!formData.message.trim()) {
      setError("Message is required");
      return;
    }

    setLoading(true);
    setError("");

    try {
      await onContact({
        ...formData,
        subject: formData.subject || defaultSubject,
      });

      onClose();
      // Reset form
      setFormData({
        subject: "",
        message: "",
        channel: "email",
        sendEmail: true,
        sendWhatsApp: false,
        applicantEmail: application?.user?.email || "",
        applicantPhone: application?.user?.profile?.phone || "",
        applicantName: application?.user?.name || "",
        jobTitle: application?.job?.title || "",
        companyName: application?.job?.company || "",
        currentUserName: currentUser?.name || "",
      });
    } catch (err: any) {
      setError(err.message || "Failed to send message");
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  const defaultSubject = `Update on your application for ${application?.job?.title} at ${application?.job?.company}`;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div
        className={`rounded-xl max-w-md w-full max-h-[90vh] overflow-y-auto ${
          isDark ? "bg-gray-800" : "bg-white"
        }`}
      >
        <div className="p-6">
          <div className="flex justify-between items-center mb-4">
            <h2
              className={`text-xl font-semibold ${
                isDark ? "text-white" : "text-gray-900"
              }`}
            >
              Contact Applicant
            </h2>
            <button
              onClick={onClose}
              className={
                isDark
                  ? "text-gray-400 hover:text-gray-300"
                  : "text-gray-400 hover:text-gray-600"
              }
            >
              ✕
            </button>
          </div>

          {application && (
            <div
              className={`mb-4 p-3 rounded-lg ${
                isDark
                  ? "bg-green-900/20 border border-green-800"
                  : "bg-green-50"
              }`}
            >
              <p
                className={`font-medium ${
                  isDark ? "text-green-300" : "text-green-800"
                }`}
              >
                {application.user?.name || "Unknown Applicant"}
              </p>
              <p
                className={`text-sm ${
                  isDark ? "text-green-400" : "text-green-600"
                }`}
              >
                {application.user?.email || "No email provided"}
              </p>
              {application.user?.profile?.phone && (
                <p
                  className={`text-sm ${
                    isDark ? "text-green-400" : "text-green-600"
                  }`}
                >
                  Phone: {application.user.profile.phone}
                </p>
              )}
              <p
                className={`text-sm ${
                  isDark ? "text-green-400" : "text-green-600"
                }`}
              >
                Applied for: {application.job?.title} at{" "}
                {application.job?.company}
              </p>
            </div>
          )}

          {/* Add channel selection */}
          <div className="mb-4">
            <label
              className={`block text-sm font-medium mb-2 ${isDark ? "text-gray-300" : "text-gray-700"}`}
            >
              Send Via
            </label>
            <div className="flex space-x-4">
              <button
                type="button"
                onClick={() => handleChannelChange("email")}
                className={`px-4 py-2 rounded-lg transition-colors ${
                  formData.channel === "email"
                    ? "bg-green-500 text-white"
                    : isDark
                      ? "bg-gray-700 text-gray-300"
                      : "bg-gray-100 text-gray-700"
                }`}
              >
                📧 Email Only
              </button>
              <button
                type="button"
                onClick={() => handleChannelChange("whatsapp")}
                className={`px-4 py-2 rounded-lg transition-colors ${
                  formData.channel === "whatsapp"
                    ? "bg-green-500 text-white"
                    : isDark
                      ? "bg-gray-700 text-gray-300"
                      : "bg-gray-100 text-gray-700"
                }`}
              >
                💬 WhatsApp Only
              </button>
              <button
                type="button"
                onClick={() => handleChannelChange("both")}
                className={`px-4 py-2 rounded-lg transition-colors ${
                  formData.channel === "both"
                    ? "bg-green-500 text-white"
                    : isDark
                      ? "bg-gray-700 text-gray-300"
                      : "bg-gray-100 text-gray-700"
                }`}
              >
                📧💬 Both
              </button>
            </div>
            <p
              className={`text-sm mt-1 ${isDark ? "text-gray-400" : "text-gray-600"}`}
            >
              {formData.channel === "email" &&
                "Message will be sent via email only"}
              {formData.channel === "whatsapp" &&
                "Message will be sent via WhatsApp only"}
              {formData.channel === "both" &&
                "Message will be sent via both email and WhatsApp"}
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Show phone input when WhatsApp is selected and no phone exists */}
            {formData.sendWhatsApp && !application?.user?.profile?.phone && (
              <div>
                <label
                  className={`block text-sm font-medium mb-1 ${isDark ? "text-gray-300" : "text-gray-700"}`}
                >
                  Applicant Phone Number *
                </label>
                <input
                  type="tel"
                  value={formData.applicantPhone}
                  onChange={(e) =>
                    setFormData({ ...formData, applicantPhone: e.target.value })
                  }
                  placeholder="0712 345 678 or +254712345678"
                  className={`w-full border rounded-lg p-2 focus:ring-2 focus:ring-green-500 focus:border-transparent ${
                    isDark
                      ? "bg-gray-700 border-gray-600 text-white placeholder-gray-400"
                      : "border-gray-300 text-gray-900"
                  }`}
                  required={formData.sendWhatsApp}
                />
                <p
                  className={`text-xs mt-1 ${isDark ? "text-gray-400" : "text-gray-600"}`}
                >
                  Format: 0712 345 678 or +254712345678
                </p>
              </div>
            )}

            {/* Show email input when email is selected and no email exists */}
            {formData.sendEmail && !application?.user?.email && (
              <div>
                <label
                  className={`block text-sm font-medium mb-1 ${isDark ? "text-gray-300" : "text-gray-700"}`}
                >
                  Applicant Email *
                </label>
                <input
                  type="email"
                  value={formData.applicantEmail}
                  onChange={(e) =>
                    setFormData({ ...formData, applicantEmail: e.target.value })
                  }
                  placeholder="Enter applicant email address"
                  className={`w-full border rounded-lg p-2 focus:ring-2 focus:ring-green-500 focus:border-transparent ${
                    isDark
                      ? "bg-gray-700 border-gray-600 text-white placeholder-gray-400"
                      : "border-gray-300 text-gray-900"
                  }`}
                  required={formData.sendEmail}
                />
              </div>
            )}

            <div>
              <label
                className={`block text-sm font-medium mb-1 ${
                  isDark ? "text-gray-300" : "text-gray-700"
                }`}
              >
                Subject
              </label>
              <input
                type="text"
                value={formData.subject || defaultSubject}
                onChange={(e) =>
                  setFormData({ ...formData, subject: e.target.value })
                }
                placeholder={defaultSubject}
                className={`w-full border rounded-lg p-2 focus:ring-2 focus:ring-green-500 focus:border-transparent ${
                  isDark
                    ? "bg-gray-700 border-gray-600 text-white placeholder-gray-400"
                    : "border-gray-300 text-gray-900"
                }`}
              />
            </div>

            <div>
              <label
                className={`block text-sm font-medium mb-1 ${
                  isDark ? "text-gray-300" : "text-gray-700"
                }`}
              >
                Message *
              </label>
              <textarea
                value={formData.message}
                onChange={(e) =>
                  setFormData({ ...formData, message: e.target.value })
                }
                rows={6}
                placeholder="Type your message to the applicant..."
                className={`w-full border rounded-lg p-2 focus:ring-2 focus:ring-green-500 focus:border-transparent ${
                  isDark
                    ? "bg-gray-700 border-gray-600 text-white placeholder-gray-400"
                    : "border-gray-300 text-gray-900"
                }`}
                required
              />
            </div>

            {error && (
              <div
                className={`p-3 rounded-lg text-sm ${
                  isDark
                    ? "bg-red-900/20 text-red-300"
                    : "bg-red-100 text-red-700"
                }`}
              >
                {error}
              </div>
            )}

            <div className="flex gap-3 pt-4">
              <button
                type="button"
                onClick={onClose}
                className={`flex-1 px-4 py-2 border rounded-lg transition-colors ${
                  isDark
                    ? "border-gray-600 text-gray-300 hover:bg-gray-700"
                    : "border-gray-300 text-gray-700 hover:bg-gray-50"
                }`}
                disabled={loading}
              >
                Cancel
              </button>

              <button
                type="submit"
                disabled={
                  loading ||
                  !formData.message.trim() ||
                  (formData.sendEmail && !formData.applicantEmail?.trim()) ||
                  (formData.sendWhatsApp && !formData.applicantPhone?.trim())
                }
                className="flex-1 px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-2"
              >
                {loading ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    Sending...
                  </>
                ) : (
                  <>
                    <svg
                      className="w-4 h-4"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8"
                      />
                    </svg>
                    Send Message
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
